import React, { useState } from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Divider, FieldGroup, Grid, Link, Modal, PillButton, Row, Stack, Text, TextInput } from 'library/ui';
import useLoading from 'library/hooks/useLoading';
import CopyButton from 'internal/shared/CopyButton';
import { client } from 'shared';
import { ShiftProviderUser } from 'models';

const CLEARINGHOUSE_URL = 'https://crw.flclearinghouse.com/';

interface RevealData {
    full_name: string;
    first_name: string;
    last_name: string;
    ssn: string;
    dob: string;
    place_of_birth: string;
    address: {
        street: string;
        city: string;
        state: string;
        zip: string;
    };
    phone: string;
    email: string;
    sex: string;
    race: string;
    height: number;
    weight: number;
    hair_color: string;
    eye_color: string;
    prior_states: string[];
}

const Field = ({ label, value, copyable }: { label: string; value: React.ReactNode; copyable?: string }) => (
    <Stack gap="none">
        <Text size="xs" uppercase weight="semibold" tone="secondary">
            {label}
        </Text>
        <Row gap="sm" align="center">
            <Text weight="medium">{value || '—'}</Text>
            {copyable ? <CopyButton value={copyable} /> : null}
        </Row>
    </Stack>
);

const SsnField = ({ ssn }: { ssn: string }) => (
    <SsnCallout>
        <Text size="xs" uppercase weight="semibold" tone="secondary">
            Social Security Number
        </Text>
        <Row gap="sm" align="center">
            <Text size="xl" weight="bold">
                {ssn}
            </Text>
            <CopyButton value={ssn} />
        </Row>
    </SsnCallout>
);

const DisplayData = ({ data, minimal }: { data: RevealData; minimal?: boolean }) => {
    if (minimal) {
        return (
            <Stack gap="md">
                <SsnField ssn={data.ssn} />
                <Grid columns={2} gap="md" collapseAt="sm">
                    <Field label="First Name" value={data.first_name} copyable={data.first_name} />
                    <Field label="Last Name" value={data.last_name} copyable={data.last_name} />
                    <Field label="Date of Birth" value={data.dob} copyable={data.dob} />
                    <Field label="Place of Birth" value={data.place_of_birth} copyable={data.place_of_birth} />
                </Grid>
                <Field
                    label="Address"
                    value={`${data.address.street}, ${data.address.city}, ${data.address.state} ${data.address.zip}`}
                />
                <Field label="States Resided In Past 5 Years" value={data.prior_states.join(', ')} />
            </Stack>
        );
    }
    return (
        <Stack gap="md">
            <Text size="lg" weight="bold">
                {data.full_name}
            </Text>
            <SsnField ssn={data.ssn} />
            <Divider />
            <Grid columns={2} gap="md" collapseAt="sm">
                <Field label="Date of Birth" value={data.dob} />
                <Field label="Place of Birth" value={data.place_of_birth} />
                <Field label="Phone" value={data.phone} />
                <Field label="Email" value={data.email} />
                <Field label="Sex" value={data.sex} />
                <Field label="Race" value={data.race} />
                <Field label="Height" value={data.height} />
                <Field label="Weight" value={data.weight} />
                <Field label="Hair Color" value={data.hair_color} />
                <Field label="Eye Color" value={data.eye_color} />
            </Grid>
            <Divider />
            <Field
                label="Address"
                value={`${data.address.street}, ${data.address.city}, ${data.address.state} ${data.address.zip}`}
            />
            <Field label="States Resided In Past 5 Years" value={data.prior_states.join(', ')} />
        </Stack>
    );
};

const SsnCallout = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '10px 14px',
    borderRadius: 10,
    backgroundColor: Colors.surface,
    border: `1px solid ${Colors.border}`,
});

export default function RevealModal({
    open,
    onClose,
    provider,
    minimal,
}: {
    open: boolean;
    onClose: () => void;
    provider: ShiftProviderUser;
    minimal?: boolean;
}) {
    const { loading, setLoading } = useLoading();
    const [code, setCode] = useState<string>('');
    const [data, setData] = useState<RevealData>();
    const [error, setError] = useState<string>();
    function submit() {
        setError(undefined);
        setLoading(true);
        client(`api/business/reveal/`, { method: 'POST', body: { provider: provider.id, code: code } })
            .then((response) => {
                setData(response);
            })
            .catch((error) => {
                setError(error['error']);
            })
            .finally(() => {
                setLoading(false);
            });
    }
    const name = `${provider.first_name} ${provider.last_name}`;
    const revealed = !!data;
    const title = revealed ? `${name}'s Information` : `Reveal ${name}'s SSN?`;
    const body: React.ReactNode = revealed ? (
        minimal ? (
            <>
                Use this information to look {name} up in the{' '}
                <Link href={CLEARINGHOUSE_URL}>Clearinghouse</Link>.
            </>
        ) : (
            'This information is for background check purposes only.'
        )
    ) : (
        "We'll email you a code to access the provider's SSN, and also text it to you if your number can receive texts. Please enter the code here. Kindly note that this information is for background check purposes only."
    );

    if (!open) {
        return null;
    }

    return (
        <Modal
            title={title}
            onClose={() => {
                setData(undefined);
                onClose();
            }}
            width={500}
        >
            <Stack gap="md">
                <Text tone="secondary">{body}</Text>
                {!data && (
                    <FieldGroup label="Code">
                        <TextInput value={code} onChange={setCode} fullWidth />
                    </FieldGroup>
                )}
                {data ? (
                    <DisplayData data={data} minimal={minimal} />
                ) : (
                    <Row justify="end" fullWidth>
                        <PillButton disabled={loading} onClick={submit}>
                            {loading ? 'Revealing…' : 'Reveal'}
                        </PillButton>
                    </Row>
                )}
                {error && <Text tone="error">{error}</Text>}
            </Stack>
        </Modal>
    );
}
